// Deleting your own account (DELETE /api/v1/me; Google Play's account-deletion rule). The card sits
// in the parent app's Settings and the staff app's Me tab; /delete-account is the public web page
// the store listings link to, which signs the person in first.
import { useState } from "react";
import { Banner, Button, Card, Spinner, TextField } from "@vavr/ui";
import { api, ApiError, navigate } from "../shared/index.ts";
import { useAsync } from "../shared/useAsync.ts";
import { AuthLayout } from "./AuthLayout.tsx";

// Where the magic-link page goes after signing in, when the person started from /delete-account.
// localStorage, because the email link opens a new tab; honoured for 30 minutes.
const AFTER_MAGIC_KEY = "pmu.after_magic";
function rememberAfterMagic(path: string) {
  try { localStorage.setItem(AFTER_MAGIC_KEY, JSON.stringify({ path, at: Date.now() })); } catch { /* the link then opens the parent pages, which have the same card */ }
}
/** The page to open after a magic-link sign-in, once. */
export function takeAfterMagic(): string | null {
  try {
    const v = localStorage.getItem(AFTER_MAGIC_KEY);
    localStorage.removeItem(AFTER_MAGIC_KEY);
    const r = v ? (JSON.parse(v) as { path: string; at: number }) : null;
    return r && r.path === "/delete-account" && Date.now() - r.at < 30 * 60_000 ? r.path : null;
  } catch { return null; }
}

/** `disabledWhy`: shown in place of the button when deleting cannot happen yet (staff changes waiting to send). */
export function DeleteAccountCard({ email, onDeleted, disabledWhy }: { email: string; onDeleted: () => void; disabledWhy?: string | null }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const matches = typed.trim().toLowerCase() === email.toLowerCase();
  const submit = async () => {
    setBusy(true); setError(null);
    try { await api("DELETE", "/api/v1/me", { confirm_email: typed }); onDeleted(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Your account was not deleted. Try again."); }
    finally { setBusy(false); }
  };
  return (
    <Card title="Delete your account" description="Signs you out everywhere and removes your name, email, phone number, passkeys and notifications from Vavr.">
      {disabledWhy ? <><Button variant="secondary" disabled fullWidth>Delete my account</Button><p className="pmu-small pmu-muted">{disabledWhy}</p></>
        : !open ? <Button variant="secondary" onClick={() => setOpen(true)} fullWidth>Delete my account</Button> : (
        <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); if (matches) void submit(); }}>
          <ul className="pmu-g-bullets">
            <li>Adults you authorised can no longer collect, and your one-time codes stop working.</li>
            <li>Your school keeps its pickup and drop-off records for as long as the law requires. Ask the school about them.</li>
            <li>This cannot be undone. The school can invite you again later.</li>
          </ul>
          {error && <Banner tone="danger" title={error} />}
          <TextField label={`Type ${email} to confirm`} type="email" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
          <div className="pmu-row">
            <Button type="submit" variant="danger" loading={busy} disabled={!matches}>Delete my account</Button>
            <Button variant="ghost" onClick={() => { setOpen(false); setTyped(""); setError(null); }}>Cancel</Button>
          </div>
        </form>
      )}
    </Card>
  );
}

/** /delete-account: the page the store listings and vavr.us link to. */
export function DeleteAccountPage() {
  const me = useAsync(() => api<{ account: { email: string } }>("GET", "/api/v1/me").catch(() => null), []);
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  if (done) {
    return <AuthLayout title="Your account was deleted"><Banner tone="success" title="You were signed out everywhere.">We sent a confirmation to your email. Your school keeps its own pickup records for the time the law requires.</Banner></AuthLayout>;
  }
  if (me.loading) return <AuthLayout title="Delete your Vavr account"><div className="pmu-center"><Spinner label="Loading" /></div></AuthLayout>;
  if (me.data) {
    return (
      <AuthLayout title="Delete your Vavr account" description={`Signed in as ${me.data.account.email}.`}>
        <DeleteAccountCard email={me.data.account.email} onDeleted={() => setDone(true)} />
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Delete your Vavr account" description="Sign in first, so we know the account is yours. You can also delete it in the app: Settings (parents) or Me (staff).">
      <div className="pmu-stack">
        <Card title="Parents and guardians" description="We email you a sign-in link. Open it on this device to come back here.">
          {sent ? <Banner tone="success" title="Check your email">If this address has a Vavr account, a sign-in link is on its way.</Banner> : (
            <form className="pmu-stack" onSubmit={async (e) => {
              e.preventDefault();
              rememberAfterMagic("/delete-account");
              await api("POST", "/api/v1/auth/magic", { email, client: "web" }).catch(() => undefined);
              setSent(true);
            }}>
              <TextField label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              <Button type="submit" fullWidth>Email me a sign-in link</Button>
            </form>
          )}
        </Card>
        <Card title="School staff">
          <Button variant="secondary" onClick={() => navigate("/login?next=/delete-account")} fullWidth>Sign in with your password</Button>
        </Card>
        <p className="pmu-small pmu-muted">An organization's only administrator must make someone else an administrator first.</p>
      </div>
    </AuthLayout>
  );
}
